/**
 * Telegram Mini App 运行时 bootstrap（ADR-0004）。
 *
 * 本模块只做"**读** initData + themeParams + language_code" —— **不**发网络请求、**不**签 JWT。
 * Auth 交换逻辑（`POST /api/v1/auth/telegram` → session token）由 api-server 实现后，
 * 在独立 module `lib/auth.ts` 消费此处暴露的 initDataRaw。
 */

import type { Locale } from '@sabay/i18n';

interface TelegramWebAppUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: TelegramWebAppUser;
    auth_date?: number;
    hash?: string;
  };
  themeParams: Record<string, string | undefined>;
  colorScheme: 'light' | 'dark';
  ready: () => void;
  expand: () => void;
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  MainButton: {
    show: () => void;
    hide: () => void;
    setText: (text: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  /**
   * Telegram WebApp >= 6.2 native alert. Displays a native dialog with a single OK button.
   * Optional in older clients — guard with feature detection.
   */
  showAlert?: (message: string, callback?: () => void) => void;
  /** Telegram WebApp >= 6.2 light haptic feedback. Optional. */
  HapticFeedback?: {
    impactOccurred?: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred?: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged?: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export interface TelegramContext {
  isInsideTelegram: boolean;
  initDataRaw: string;         // 传给 api-server 做 HMAC 校验
  user?: TelegramWebAppUser;
  locale: Locale;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string | undefined>;
}

const LOCALE_MAP: Record<string, Locale> = {
  zh: 'zh-CN',
  'zh-CN': 'zh-CN',
  'zh-Hans': 'zh-CN',
  km: 'km-KH',
  'km-KH': 'km-KH',
  en: 'en',
  'en-US': 'en',
  'en-GB': 'en',
};

export function resolveLocale(languageCode: string | undefined): Locale {
  if (!languageCode) return 'zh-CN';
  return LOCALE_MAP[languageCode] ?? 'zh-CN';
}

/**
 * 在 app mount 时调用。同步返回，不抛错。
 * 在非 Telegram 环境（本地 `vite dev` 浏览器打开）会返回 `isInsideTelegram=false` 的兜底 context。
 */
export function readTelegramContext(): TelegramContext {
  if (typeof window === 'undefined' || !window.Telegram?.WebApp) {
    return {
      isInsideTelegram: false,
      initDataRaw: '',
      locale: 'zh-CN',
      colorScheme: 'light',
      themeParams: {},
    };
  }

  const wa = window.Telegram.WebApp;
  try {
    wa.ready();
    wa.expand();
  } catch {
    // ignore — some older Telegram clients don't support these
  }

  return {
    isInsideTelegram: true,
    initDataRaw: wa.initData,
    user: wa.initDataUnsafe.user,
    locale: resolveLocale(wa.initDataUnsafe.user?.language_code),
    colorScheme: wa.colorScheme,
    themeParams: wa.themeParams,
  };
}

/**
 * 显示一个轻量级提示。优先使用 Telegram WebApp `showAlert`（原生弹层），
 * 在非 Telegram 环境（本地 vite dev）退化到 `window.alert`。
 *
 * Used by:
 * - 顶部一级 nav 中尚未上线的入口（二手市场 / 租车 / 地产 / 票务·卡券）
 * - CategoryRow 中 `comingSoon: true` 的 placeholder tile
 *
 * 不发起任何后端请求；不做埋点（埋点由调用方 `trackTopNavComingSoon` 之类负责）。
 */
export function showInlineAlert(message: string): void {
  if (typeof window === 'undefined') return;
  const wa = window.Telegram?.WebApp;
  const showAlert = (wa as unknown as { showAlert?: (m: string) => void } | undefined)?.showAlert;
  if (typeof showAlert === 'function' && wa) {
    try {
      showAlert.call(wa, message);
      // light haptic when available — subtle confirmation that the tap registered
      const haptic = wa.HapticFeedback?.impactOccurred;
      if (typeof haptic === 'function' && wa.HapticFeedback) {
        try {
          haptic.call(wa.HapticFeedback, 'light');
        } catch {
          // ignore
        }
      }
      return;
    } catch {
      // fall through to window.alert
    }
  }
  window.alert(message);
}

/** 把 Telegram themeParams 映射到 `:root` CSS variables（ADR-0004 §4.3） */
export function applyThemeParams(params: Record<string, string | undefined>): void {
  const root = document.documentElement;
  const map: Record<string, string> = {
    bg_color: '--tg-bg',
    text_color: '--tg-text',
    hint_color: '--tg-hint',
    link_color: '--tg-link',
    button_color: '--tg-button',
    button_text_color: '--tg-button-text',
  };
  for (const [tgKey, cssVar] of Object.entries(map)) {
    const v = params[tgKey];
    if (v) root.style.setProperty(cssVar, v);
  }
}
